import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { templates } from '../data/templates';
import axios from 'axios';
import {
  Sparkles, Lock, ArrowRight, Crown,
  Star, Zap, LogOut, Play, BookOpen,
  FileText, Trash2, Loader2, LayoutGrid, Search, X,
  TrendingUp, Clock, Pin
} from 'lucide-react';

import LumeraLogo from '../components/LumeraLogo';
import { openPinterestStudio } from '../utils/pinterestStudioHelper';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Curated Trending Template IDs ─── */
const TRENDING_IDS = [
  'luxury-brand-story', 'conversion-carousel', 'launch-like-a-pro',
  'viral-hook', 'wedding-planner', 'high-ticket-sales-flow',
  'recipe-collection', 'smart-tips-carousel'
];

/* ─── Recently Used helpers (localStorage) ─── */
const RECENT_KEY = 'lumera_recent_templates';
const getRecentlyUsed = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
};
const trackTemplateUse = (templateId) => {
  const recent = getRecentlyUsed().filter(id => id !== templateId);
  recent.unshift(templateId);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 8)));
};

/* ─── Compact Spotlight Card (for Trending / Recently Used rows) ─── */
const SpotlightCard = ({ template, isPro: userIsPro, onUse, onUpgrade }) => {
  const isLocked = template.isPro && !userIsPro;
  return (
    <div
      data-testid={`spotlight-card-${template.id}`}
      className="flex-shrink-0 w-[220px] group rounded-2xl border border-stone-100 bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
      onClick={() => isLocked ? onUpgrade() : onUse(template)}
    >
      <div className="relative h-[140px] overflow-hidden">
        {template.preview ? (
          <img src={template.preview} alt={template.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full" style={{ backgroundColor: template.previewColors?.[0] || '#F5F0EB' }} />
        )}
        {template.badge && (
          <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/90 backdrop-blur-sm text-stone-700 shadow-sm">
            {template.badge}
          </span>
        )}
        {isLocked && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Lock className="w-4 h-4 text-white" />
          </div>
        )}
        {template.isPro && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 bg-stone-900/70 backdrop-blur-sm text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            <Crown className="w-2.5 h-2.5" /> PRO
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-bold text-stone-800 text-sm leading-tight truncate">{template.name}</h4>
        <p className="text-[11px] font-sans text-stone-400 mt-0.5 capitalize">{template.useCase} &middot; {template.slides} slides</p>
      </div>
    </div>
  );
};

/* ─── Animated Slide Preview (shows on hover) ─── */
const AnimatedSlidePreview = ({ template, isHovered }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = template.preFill.structure || [];
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isHovered && slides.length > 0) {
      setCurrentSlide(0);
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
      }, 2000);
    } else {
      clearInterval(intervalRef.current);
      setCurrentSlide(0);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovered, slides.length]);

  if (!isHovered || slides.length === 0) return null;

  const [bg, text, accent] = template.previewColors;
  const isDark = bg === '#1A1A1A';
  const slide = slides[currentSlide];
  const typeLabel = slide?.type?.toUpperCase() || 'SLIDE';
  const instruction = slide?.instruction || '';

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center animate-fade-in" style={{ backgroundColor: bg }}>
      <div className="w-full h-full flex flex-col items-center justify-center p-6 md:p-8 text-center">
        <span className="text-[10px] font-bold tracking-widest px-3 py-1 rounded-full mb-4 uppercase" style={{ backgroundColor: accent + '25', color: isDark ? '#F5F0EB' : accent }}>{typeLabel}</span>
        <span className="text-xs font-medium mb-3 opacity-50" style={{ color: isDark ? '#F5F0EB' : text }}>Slide {currentSlide + 1} of {slides.length}</span>
        <p className="font-serif font-semibold text-lg md:text-xl leading-snug max-w-[85%] transition-opacity duration-500" style={{ color: isDark ? '#F5F0EB' : text }}>{instruction}</p>
        <div className="flex items-center gap-1.5 mt-6">
          {slides.map((_, i) => (
            <div key={i} className="rounded-full transition-all duration-300" style={{ width: i === currentSlide ? '16px' : '5px', height: '5px', backgroundColor: i === currentSlide ? accent : (isDark ? '#F5F0EB30' : text + '25') }} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Template Card (built-in templates) ─── */
const TemplateCard = ({ template, isPro: userIsPro, onUse, onUpgrade }) => {
  const isLocked = template.isPro && !userIsPro;
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  return (
    <div data-testid={`template-card-${template.id}`}
      className={`group relative bg-white rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1.5 ${template.isPro ? 'border-stone-200/80 hover:shadow-[0_16px_48px_rgba(232,108,84,0.15)] hover:border-primary/30' : 'border-stone-100 hover:shadow-[0_16px_48px_rgba(0,0,0,0.1)]'}`}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {template.badge && (
        <div className="absolute top-4 left-4 z-30">
          <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full shadow-sm ${template.badge === 'Most Popular' ? 'bg-primary text-white' : template.badge === 'Best Converting' ? 'bg-stone-900 text-white' : template.badge === 'Best for Sales' ? 'bg-accent text-white' : 'bg-secondary text-primary'}`}>
            {template.badge === 'Most Popular' && <Star className="w-3 h-3" />}
            {template.badge === 'Best Converting' && <Zap className="w-3 h-3" />}
            {template.badge}
          </span>
        </div>
      )}
      {template.isPro && (
        <div className="absolute top-4 right-4 z-30">
          <span className="inline-flex items-center gap-1 bg-stone-900/80 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-full"><Crown className="w-3 h-3" /> PRO</span>
        </div>
      )}
      <div className="relative aspect-square overflow-hidden">
        {template.preview ? (
          <img src={template.preview} alt={template.name} className={`w-full h-full object-cover transition-all duration-500 ${isHovered ? 'scale-105 opacity-0' : 'scale-100 opacity-100'}`} loading="lazy" />
        ) : (
          <div className={`w-full h-full transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
            <TemplatePreviewCSS template={template} />
          </div>
        )}
        <AnimatedSlidePreview template={template} isHovered={isHovered} />
        <div className={`absolute bottom-3 right-3 z-20 transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-60 group-hover:opacity-80'}`}>
          <div className="bg-white/90 backdrop-blur-sm rounded-full w-8 h-8 flex items-center justify-center shadow-sm"><Play className="w-3.5 h-3.5 text-stone-700 ml-0.5" fill="currentColor" /></div>
        </div>
        {isLocked && !isHovered && <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />}
        {isLocked && isHovered && (
          <div className="absolute inset-0 z-30 bg-black/30 backdrop-blur-[2px] flex items-center justify-center animate-fade-in">
            <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-stone-800 text-sm font-semibold px-5 py-2.5 rounded-full shadow-lg"><Lock className="w-3.5 h-3.5" /> Upgrade to unlock</span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-bold text-stone-800 text-base leading-tight mb-1.5">{template.name}</h3>
        <p className="text-sm font-sans text-stone-500 leading-relaxed mb-4 line-clamp-2">{template.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-stone-400">{template.slides} {template.slides === 1 ? 'slide' : 'slides'}</span>
            <button
              data-testid={`pinterest-template-${template.id}`}
              onClick={(e) => {
                e.stopPropagation();
                openPinterestStudio(navigate, { title: template.name, description: template.description, category: template.useCase });
              }}
              className="p-1.5 rounded-lg text-stone-300 hover:text-[#E60023] hover:bg-[#E60023]/5 transition-all"
              title="Promote on Pinterest"
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
          </div>
          {isLocked ? (
            <Button data-testid={`unlock-template-${template.id}`} onClick={onUpgrade} className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-4 py-2 text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-300 h-auto">
              <Lock className="w-3 h-3 mr-1.5" /> Unlock with Pro
            </Button>
          ) : (
            <Button data-testid={`use-template-${template.id}`} onClick={() => onUse(template)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 py-2 text-xs font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 h-auto">
              Use Template <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── CSS-based Preview (static fallback) ─── */
const TemplatePreviewCSS = ({ template }) => {
  const [bg, text, accent] = template.previewColors;
  const isDark = bg === '#1A1A1A';
  return (
    <div className="w-full h-full flex items-center justify-center p-8" style={{ backgroundColor: bg }}>
      <div className="w-full max-w-[200px] space-y-3">
        <div className="rounded-2xl p-4 shadow-lg mx-auto" style={{ backgroundColor: isDark ? '#2C2C2C' : '#FFFFFF', maxWidth: '160px' }}>
          <div className="flex items-center gap-1.5 mb-3">
            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: accent + '40' }} />
            <div className="flex-1"><div className="h-1.5 rounded-full w-12" style={{ backgroundColor: text + '30' }} /></div>
          </div>
          <div className="mb-2"><div className="h-2.5 rounded-full w-full mb-1.5" style={{ backgroundColor: text + '80' }} /><div className="h-2.5 rounded-full w-3/4" style={{ backgroundColor: text + '60' }} /></div>
          <div className="h-px my-2" style={{ backgroundColor: accent + '50' }} />
          <div className="space-y-1.5"><div className="h-1.5 rounded-full w-full" style={{ backgroundColor: text + '20' }} /><div className="h-1.5 rounded-full w-5/6" style={{ backgroundColor: text + '20' }} /><div className="h-1.5 rounded-full w-4/6" style={{ backgroundColor: text + '20' }} /></div>
          <div className="h-6 rounded-full mt-3 flex items-center justify-center" style={{ backgroundColor: accent }}><div className="h-1.5 rounded-full w-8" style={{ backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF' }} /></div>
        </div>
        <div className="flex items-center justify-center gap-1"><div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} /><div className="w-1 h-1 rounded-full" style={{ backgroundColor: text + '30' }} /><div className="w-1 h-1 rounded-full" style={{ backgroundColor: text + '30' }} /></div>
      </div>
    </div>
  );
};

/* ─── My Template Card (user-saved templates) ─── */
const MyTemplateCard = ({ template, onUse, onDelete, deleting }) => {
  const isCarousel = template.is_carousel;
  const pageCount = template.page_count || 0;
  const accentColor = isCarousel ? '#B49A88' : '#A3B18A';

  return (
    <div data-testid={`my-template-card-${template.id}`} className="group bg-white rounded-2xl border border-stone-100 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]">
      {/* Preview area */}
      <div className="relative aspect-[4/3] overflow-hidden" style={{ backgroundColor: accentColor + '10' }}>
        <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: accentColor + '15' }}>
            {isCarousel ? <LayoutGrid className="w-7 h-7" style={{ color: accentColor }} strokeWidth={1.5} /> : <FileText className="w-7 h-7" style={{ color: accentColor }} strokeWidth={1.5} />}
          </div>
          <p className="font-serif font-semibold text-stone-800 text-sm leading-snug max-w-[85%]">{template.name}</p>
          <span className="text-[10px] font-bold tracking-widest uppercase mt-2 px-2.5 py-0.5 rounded-full" style={{ backgroundColor: accentColor + '15', color: accentColor }}>
            {isCarousel ? `${pageCount} slides` : `${pageCount} pages`}
          </span>
        </div>
        {/* Delete button */}
        <button
          data-testid={`delete-my-template-${template.id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
          disabled={deleting}
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-full w-8 h-8 flex items-center justify-center shadow-sm hover:bg-red-50 text-stone-400 hover:text-red-500"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: accentColor + '12', color: accentColor }}>{template.category}</span>
          {template.used_count > 0 && <span className="text-[10px] font-sans text-stone-400">Used {template.used_count}x</span>}
        </div>
        <p className="text-xs font-sans text-stone-500 line-clamp-2 mb-4 leading-relaxed">{template.description || 'No description'}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-sans text-stone-400">{new Date(template.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          <Button data-testid={`use-my-template-${template.id}`} onClick={() => onUse(template)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 py-2 text-xs font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 h-auto">
            Remix <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Templates Page ─── */
const Templates = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro, logout } = useAuth();
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('gallery');
  const [myTemplates, setMyTemplates] = useState([]);
  const [loadingMy, setLoadingMy] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [myFilter, setMyFilter] = useState('all');
  const [recentIds, setRecentIds] = useState(() => getRecentlyUsed());

  const fetchMyTemplates = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingMy(true);
    try {
      const res = await axios.get(`${API}/templates/`);
      setMyTemplates(res.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
    setLoadingMy(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'my' && isAuthenticated) {
      fetchMyTemplates();
    }
  }, [activeTab, isAuthenticated, fetchMyTemplates]);

  const filteredTemplates = templates.filter(t => {
    const passType = filter === 'all' || (filter === 'free' ? !t.isPro : t.isPro);
    const passCategory = categoryFilter === 'all' || t.useCase === categoryFilter;
    const q = searchQuery.toLowerCase().trim();
    const passSearch = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q) || t.useCase.toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q);
    return passType && passCategory && passSearch;
  });

  const filteredMyTemplates = myTemplates.filter(t => {
    if (myFilter === 'carousel') return t.is_carousel;
    if (myFilter === 'document') return !t.is_carousel;
    return true;
  });

  const myCategories = [...new Set(myTemplates.map(t => t.category))];

  /* Computed spotlight lists */
  const trendingTemplates = TRENDING_IDS.map(id => templates.find(t => t.id === id)).filter(Boolean);
  const recentTemplates = recentIds.map(id => templates.find(t => t.id === id)).filter(Boolean);
  const showSpotlights = filter === 'all' && categoryFilter === 'all' && !searchQuery.trim();

  const handleUseTemplate = (template) => {
    if (!isAuthenticated) { navigate('/register'); return; }
    trackTemplateUse(template.id);
    setRecentIds(getRecentlyUsed());
    const params = new URLSearchParams({
      template: template.id, type: template.preFill.productType,
      tone: template.preFill.tone, length: template.preFill.length,
      slides: template.preFill.slideCount.toString()
    });
    navigate(`/generator?${params.toString()}`);
  };

  const handleUseMyTemplate = async (template) => {
    try {
      const res = await axios.post(`${API}/templates/${template.id}/use`);
      const data = res.data;
      // Navigate to generator with the template structure loaded
      navigate('/generator', {
        state: {
          remixTemplate: true,
          templateStructure: data.structure,
          productType: data.product_type,
          templateName: data.name,
        }
      });
    } catch (err) {
      console.error('Failed to use template:', err);
    }
  };

  const handleDeleteMyTemplate = async (templateId) => {
    setDeletingTemplate(templateId);
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setMyTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
    setDeletingTemplate(null);
  };

  const handleUpgrade = () => navigate('/pricing');

  return (
    <div data-testid="templates-page" className="min-h-screen bg-[#F7F2EC] dark:bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/')} className="text-sm font-semibold text-stone-700 hover:text-primary transition-colors">Home</button>
            <button className="text-sm font-semibold text-primary transition-colors">Templates</button>
            <button onClick={() => navigate('/pricing')} className="text-sm font-semibold text-stone-700 hover:text-primary transition-colors">Pricing</button>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-stone-700 font-medium text-sm">Dashboard</Button>
                <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="text-stone-500"><LogOut className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-stone-700 font-medium">Log in</Button>
                <Button onClick={() => navigate('/register')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">Start Free</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-sm font-medium tracking-wide uppercase text-[#B49A88] mb-4 block">Templates</span>
          <h1 className="text-4xl md:text-6xl font-bold text-stone-900 tracking-tight mb-4">
            Ready-made templates that <span className="italic text-primary">convert</span>
          </h1>
          <p className="text-lg font-sans text-stone-600 max-w-2xl mx-auto mb-8">
            Choose a template, customize it with AI, and start selling. Hover to preview each slide structure.
          </p>

          {/* Search Bar */}
          <div data-testid="template-search" className="relative max-w-md mx-auto mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              data-testid="template-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates by name, topic, or category..."
              className="w-full pl-11 pr-10 py-3 rounded-full border border-stone-200 bg-white text-sm font-sans text-stone-800 placeholder:text-stone-400 focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none shadow-sm transition-all"
            />
            {searchQuery && (
              <button
                data-testid="template-search-clear"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-500 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Tab selector: Gallery / My Templates */}
          <div className="inline-flex items-center bg-white border border-stone-200 rounded-full p-1 shadow-sm mb-6">
            <button data-testid="tab-gallery" onClick={() => setActiveTab('gallery')}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${activeTab === 'gallery' ? 'bg-primary text-white shadow-md' : 'text-stone-600 hover:text-stone-800'}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Gallery
            </button>
            {isAuthenticated && (
              <button data-testid="tab-my-templates" onClick={() => setActiveTab('my')}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${activeTab === 'my' ? 'bg-primary text-white shadow-md' : 'text-stone-600 hover:text-stone-800'}`}>
                <BookOpen className="w-3.5 h-3.5" /> My Templates
                {myTemplates.length > 0 && activeTab !== 'my' && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold">{myTemplates.length}</span>
                )}
              </button>
            )}
          </div>

          {/* Gallery filters */}
          {activeTab === 'gallery' && (
            <div className="space-y-3">
              <div data-testid="template-filters" className="inline-flex items-center bg-white border border-stone-200 rounded-full p-1 shadow-sm">
                {[
                  { value: 'all', label: 'All', count: templates.length },
                  { value: 'free', label: 'Free', count: templates.filter(t => !t.isPro).length },
                  { value: 'pro', label: 'Pro', count: templates.filter(t => t.isPro).length }
                ].map((f) => (
                  <button key={f.value} data-testid={`filter-${f.value}`} onClick={() => setFilter(f.value)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${filter === f.value ? 'bg-primary text-white shadow-md' : 'text-stone-600 hover:text-stone-800'}`}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
              <div data-testid="category-filters" className="flex items-center justify-center gap-2 flex-wrap">
                {[
                  { value: 'all', label: 'All Categories' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'business', label: 'Business' },
                  { value: 'education', label: 'Education' },
                  { value: 'lifestyle', label: 'Lifestyle' },
                  { value: 'fitness', label: 'Fitness' },
                  { value: 'creative', label: 'Creative' },
                ].map((c) => {
                  const count = templates.filter(t => {
                    const passType = filter === 'all' || (filter === 'free' ? !t.isPro : t.isPro);
                    return c.value === 'all' ? passType : (passType && t.useCase === c.value);
                  }).length;
                  return (
                    <button key={c.value} data-testid={`category-${c.value}`} onClick={() => setCategoryFilter(c.value)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                        categoryFilter === c.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-stone-200 text-stone-500 hover:border-stone-300 hover:text-stone-700'
                      }`}>
                      {c.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* My Templates filters */}
          {activeTab === 'my' && myTemplates.length > 0 && (
            <div className="inline-flex items-center bg-white border border-stone-200 rounded-full p-1 shadow-sm">
              {[
                { value: 'all', label: 'All', count: myTemplates.length },
                { value: 'carousel', label: 'Carousels', count: myTemplates.filter(t => t.is_carousel).length },
                { value: 'document', label: 'Documents', count: myTemplates.filter(t => !t.is_carousel).length },
              ].map((f) => (
                <button key={f.value} data-testid={`my-filter-${f.value}`} onClick={() => setMyFilter(f.value)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${myFilter === f.value ? 'bg-primary text-white shadow-md' : 'text-stone-600 hover:text-stone-800'}`}>
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── Gallery Tab ─── */}
      {activeTab === 'gallery' && (
        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">

            {/* ─── Spotlight: Recently Used ─── */}
            {showSpotlights && isAuthenticated && recentTemplates.length > 0 && (
              <div data-testid="recently-used-section" className="mb-12">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-serif font-semibold text-stone-800 leading-tight">Recently Used</h2>
                    <p className="text-xs font-sans text-stone-400">Pick up where you left off</p>
                  </div>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2 scrollbar-hide">
                  {recentTemplates.map(t => (
                    <SpotlightCard key={`recent-${t.id}`} template={t} isPro={isPro} onUse={handleUseTemplate} onUpgrade={handleUpgrade} />
                  ))}
                </div>
              </div>
            )}

            {/* ─── Spotlight: Trending ─── */}
            {showSpotlights && (
              <div data-testid="trending-section" className="mb-12">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-[#4A3525]/10 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-[#B49A88]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-serif font-semibold text-stone-800 leading-tight">Trending Templates</h2>
                    <p className="text-xs font-sans text-stone-400">Most popular with creators right now</p>
                  </div>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2 scrollbar-hide">
                  {trendingTemplates.map(t => (
                    <SpotlightCard key={`trend-${t.id}`} template={t} isPro={isPro} onUse={handleUseTemplate} onUpgrade={handleUpgrade} />
                  ))}
                </div>
              </div>
            )}

            {filteredTemplates.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-sans text-stone-500">No templates match this filter. Try a different combination.</p>
              </div>
            ) : (
              <>
                {/* Show Free section if there are free templates */}
                {filteredTemplates.filter(t => !t.isPro).length > 0 && (
                  <>
                    <div className="mb-8">
                      <h2 className="text-xl font-serif font-semibold text-stone-800 mb-1">
                        Free Templates
                        {categoryFilter !== 'all' && <span className="text-primary ml-2 capitalize text-base font-medium">/ {categoryFilter}</span>}
                      </h2>
                      <p className="text-sm font-sans text-stone-500">Start creating with these templates — no upgrade needed.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
                      {filteredTemplates.filter(t => !t.isPro).map((template) => (
                        <TemplateCard key={template.id} template={template} isPro={isPro} onUse={handleUseTemplate} onUpgrade={handleUpgrade} />
                      ))}
                    </div>
                  </>
                )}
                {/* Show Pro section if there are pro templates */}
                {filteredTemplates.filter(t => t.isPro).length > 0 && (
                  <>
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-xl font-serif font-semibold text-stone-800">
                          Pro Templates
                          {categoryFilter !== 'all' && <span className="text-primary ml-2 capitalize text-base font-medium">/ {categoryFilter}</span>}
                        </h2>
                        <span className="inline-flex items-center gap-1 bg-gradient-to-r from-primary/10 to-secondary text-primary text-xs font-bold px-3 py-1 rounded-full"><Crown className="w-3 h-3" /> Premium</span>
                      </div>
                      <p className="text-sm font-sans text-stone-500">High-converting templates designed for serious creators.{!isPro && ' Upgrade to Pro to unlock all.'}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {filteredTemplates.filter(t => t.isPro).map((template) => (
                        <TemplateCard key={template.id} template={template} isPro={isPro} onUse={handleUseTemplate} onUpgrade={handleUpgrade} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* ─── My Templates Tab ─── */}
      {activeTab === 'my' && (
        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {loadingMy ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : filteredMyTemplates.length === 0 ? (
              <div data-testid="my-templates-empty" className="text-center py-20">
                <div className="w-20 h-20 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-10 h-10 text-stone-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">
                  {myTemplates.length > 0 ? 'No templates in this filter' : 'No saved templates yet'}
                </h3>
                <p className="text-sm font-sans text-stone-500 max-w-md mx-auto mb-6">
                  {myTemplates.length > 0
                    ? 'Try a different filter to see your templates.'
                    : 'Generate a product and click "Save as Template" to start building your personal library of reusable designs.'
                  }
                </p>
                {myTemplates.length === 0 && (
                  <Button onClick={() => navigate('/generator')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 font-semibold shadow-lg">
                    Create Your First Product <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            ) : (
              <>
                {myCategories.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-serif font-semibold text-stone-800 mb-1">Your Template Library</h2>
                    <p className="text-sm font-sans text-stone-500">{filteredMyTemplates.length} template{filteredMyTemplates.length !== 1 ? 's' : ''} saved. Click &ldquo;Remix&rdquo; to create a new product with the same structure.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredMyTemplates.map((template) => (
                    <MyTemplateCard
                      key={template.id}
                      template={template}
                      onUse={handleUseMyTemplate}
                      onDelete={handleDeleteMyTemplate}
                      deleting={deletingTemplate === template.id}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Upgrade CTA */}
      {!isPro && activeTab === 'gallery' && (
        <section className="py-16 bg-white border-t border-stone-100">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <Crown className="w-10 h-10 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-2xl md:text-3xl font-serif font-semibold text-stone-800 mb-3">Unlock all {templates.filter(t => t.isPro).length} Pro templates</h2>
            <p className="text-stone-600 font-sans mb-6">Get unlimited access to premium, high-converting templates plus unlimited AI generations.</p>
            <Button data-testid="templates-upgrade-btn" onClick={() => navigate('/pricing')} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-semibold px-8 py-6 rounded-full text-base">
              Upgrade to Pro <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><img src="/lumera-logo.png" alt="Lumera" className="w-6 h-6 rounded-full" /><span className="font-serif font-semibold text-foreground">Lumera</span></div>
          <p className="text-sm font-sans text-stone-500">Create and sell digital products in minutes</p>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/pricing')} className="text-sm font-sans text-stone-500 hover:text-primary transition-colors">Pricing</button>
            <button onClick={() => navigate('/login')} className="text-sm font-sans text-stone-500 hover:text-primary transition-colors">Log in</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Templates;
